import { Pin } from '@ui/shared/icons';

/**
 * "Pinned to Today sidebar" toggle (R8.8, AC-T19/AC-T20) — a genuinely
 * separate section from `VisibilityTogglesSection`, not a fourth entry in
 * it: that component is explicitly locked to exactly three toggles (AC-O23,
 * "no fourth toggle"). Pinning has no math consequence (progress/forecast/
 * XP/the 5-active-objective cap are all untouched) — this is purely a
 * display-order concern for `DesktopShell`'s Pinned Objectives sidebar
 * section. Only rendered once the objective is committed (edit path) —
 * pinning a not-yet-created draft has no meaningful target to pin.
 *
 * Ordering itself (drag-reorder) is out of scope here — per
 * design/screens/today-desktop-v2.md §3, "no drag-reorder control lives in
 * the sidebar itself... a 'Pinned' toggle + order control on [the objective
 * edit] screen is a natural home, flagged here as an implied downstream
 * need." This component closes exactly that gap: a plain on/off toggle,
 * appending to the end of the pinned order (`pinnedOrder: null`, which the
 * domain layer normalizes to sort-stable position 0 whenever nothing else
 * claims an earlier slot) rather than offering manual reordering.
 */
export function PinnedToggleSection({
  pinned,
  onChange,
}: {
  readonly pinned: boolean;
  readonly onChange: (next: boolean) => void;
}) {
  return (
    <div style={{ marginTop: 'var(--space-5)', paddingTop: 'var(--space-5)', borderTop: '1px solid var(--border-subtle)' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 'var(--space-3)' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)', minWidth: 0 }}>
          <Pin size={16} color={pinned ? 'var(--hue-primary)' : 'var(--text-muted)'} aria-hidden="true" />
          <div>
            <div style={{ fontSize: 'var(--type-body-size)', fontWeight: 500, color: 'var(--text-primary)' }}>Pinned to Today</div>
            <div style={{ marginTop: '2px', fontSize: 'var(--type-caption-size)', color: 'var(--text-secondary)' }}>
              Shows up in the Today sidebar for quick access.
            </div>
          </div>
        </div>
        <button
          type="button"
          role="switch"
          aria-checked={pinned}
          aria-label="Pinned to Today"
          onClick={() => onChange(!pinned)}
          style={{
            flex: 'none',
            width: '40px',
            height: '24px',
            borderRadius: 'var(--radius-pill)',
            background: pinned ? 'var(--hue-primary-vivid)' : 'var(--border-strong)',
            position: 'relative',
            transition: 'background var(--dur-fast) var(--ease-standard)',
          }}
        >
          <span
            aria-hidden="true"
            style={{
              position: 'absolute',
              top: '2px',
              left: pinned ? '18px' : '2px',
              width: '20px',
              height: '20px',
              borderRadius: '50%',
              background: 'var(--surface-raised)',
              transition: 'left var(--dur-fast) var(--ease-standard)',
            }}
          />
        </button>
      </div>
    </div>
  );
}
